import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  DatePicker,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Spin,
  message,
  Divider
} from 'antd';
import {
  DownloadOutlined,
  PrinterOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface TrialBalanceEntry {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  openingDebit: number;
  openingCredit: number;
  periodDebit: number;
  periodCredit: number;
  closingDebit: number;
  closingCredit: number;
}

interface TrialBalanceData {
  fromDate: string | null;
  toDate: string;
  entries: TrialBalanceEntry[];
  totals: {
    openingDebit: number;
    openingCredit: number;
    periodDebit: number;
    periodCredit: number;
    closingDebit: number;
    closingCredit: number;
  };
  balanced: boolean;
}

const formatCurrency = (amount: number) => {
  if (amount === 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'HKD',
    minimumFractionDigits: 2
  }).format(amount);
};

const getAccountTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    ASSET: 'blue',
    LIABILITY: 'orange',
    EQUITY: 'purple',
    REVENUE: 'green',
    EXPENSE: 'red'
  };
  return colors[type] || 'default';
};

const TrialBalanceReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TrialBalanceData | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('year'),
    dayjs()
  ]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        toDate: dateRange[1].format('YYYY-MM-DD'),
        ...(dateRange[0] && { fromDate: dateRange[0].format('YYYY-MM-DD') })
      });

      const response = await apiService.get(`/accounting/reports/trial-balance?${params}`);
      setData(response.data || response);
    } catch (error: any) {
      message.error(error.message || 'Failed to load trial balance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns: ColumnsType<TrialBalanceEntry> = [
    {
      title: 'Account Code',
      dataIndex: 'accountCode',
      key: 'accountCode',
      width: 120,
      fixed: 'left'
    },
    {
      title: 'Account Name',
      dataIndex: 'accountName',
      key: 'accountName',
      width: 200,
      fixed: 'left'
    },
    {
      title: 'Type',
      dataIndex: 'accountType',
      key: 'accountType',
      width: 100,
      render: (type: string) => (
        <Tag color={getAccountTypeColor(type)}>{type}</Tag>
      )
    },
    {
      title: 'Opening Balance',
      children: [
        {
          title: 'Debit',
          dataIndex: 'openingDebit',
          key: 'openingDebit',
          width: 120,
          align: 'right',
          render: (val: number) => formatCurrency(val)
        },
        {
          title: 'Credit',
          dataIndex: 'openingCredit',
          key: 'openingCredit',
          width: 120,
          align: 'right',
          render: (val: number) => formatCurrency(val)
        }
      ]
    },
    {
      title: 'Period Movement',
      children: [
        {
          title: 'Debit',
          dataIndex: 'periodDebit',
          key: 'periodDebit',
          width: 120,
          align: 'right',
          render: (val: number) => formatCurrency(val)
        },
        {
          title: 'Credit',
          dataIndex: 'periodCredit',
          key: 'periodCredit',
          width: 120,
          align: 'right',
          render: (val: number) => formatCurrency(val)
        }
      ]
    },
    {
      title: 'Closing Balance',
      children: [
        {
          title: 'Debit',
          dataIndex: 'closingDebit',
          key: 'closingDebit',
          width: 120,
          align: 'right',
          render: (val: number) => (
            <Text strong={val > 0}>{formatCurrency(val)}</Text>
          )
        },
        {
          title: 'Credit',
          dataIndex: 'closingCredit',
          key: 'closingCredit',
          width: 120,
          align: 'right',
          render: (val: number) => (
            <Text strong={val > 0}>{formatCurrency(val)}</Text>
          )
        }
      ]
    }
  ];

  const handleDateChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const handleExport = () => {
    if (!data || data.entries.length === 0) {
      message.warning('No data to export');
      return;
    }
    const rows = data.entries.map(e => ({
      'Account Code': e.accountCode,
      'Account Name': e.accountName,
      'Type': e.accountType,
      'Opening Debit': Number(e.openingDebit || 0),
      'Opening Credit': Number(e.openingCredit || 0),
      'Period Debit': Number(e.periodDebit || 0),
      'Period Credit': Number(e.periodCredit || 0),
      'Closing Debit': Number(e.closingDebit || 0),
      'Closing Credit': Number(e.closingCredit || 0),
    }));
    rows.push({
      'Account Code': '',
      'Account Name': 'TOTALS',
      'Type': '',
      'Opening Debit': Number(data.totals.openingDebit || 0),
      'Opening Credit': Number(data.totals.openingCredit || 0),
      'Period Debit': Number(data.totals.periodDebit || 0),
      'Period Credit': Number(data.totals.periodCredit || 0),
      'Closing Debit': Number(data.totals.closingDebit || 0),
      'Closing Credit': Number(data.totals.closingCredit || 0),
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 14 }, { wch: 30 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trial Balance');
    XLSX.writeFile(wb, `Trial_Balance_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    message.success('Trial Balance exported');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Trial Balance</Title>
          {data && (
            data.balanced ? (
              <Tag icon={<CheckCircleOutlined />} color="success">Balanced</Tag>
            ) : (
              <Tag icon={<WarningOutlined />} color="error">Out of Balance</Tag>
            )
          )}
        </Space>
      }
      extra={
        <Space>
          <RangePicker
            value={dateRange}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        {data && (
          <>
            <Row gutter={24} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="Opening Debit"
                  value={data.totals.openingDebit}
                  precision={2}
                  prefix="$"
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Opening Credit"
                  value={data.totals.openingCredit}
                  precision={2}
                  prefix="$"
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Closing Debit"
                  value={data.totals.closingDebit}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Closing Credit"
                  value={data.totals.closingCredit}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
            </Row>

            <Divider />

            <Table
              columns={columns}
              dataSource={data.entries}
              rowKey="accountId"
              scroll={{ x: 1200 }}
              pagination={false}
              bordered
              size="small"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong>TOTALS</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {formatCurrency(data.totals.openingDebit)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      {formatCurrency(data.totals.openingCredit)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {formatCurrency(data.totals.periodDebit)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      {formatCurrency(data.totals.periodCredit)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">
                      <Text strong style={{ color: '#1890ff' }}>
                        {formatCurrency(data.totals.closingDebit)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right">
                      <Text strong style={{ color: '#1890ff' }}>
                        {formatCurrency(data.totals.closingCredit)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Text type="secondary">
                Period: {data.fromDate ? dayjs(data.fromDate).format('MMM DD, YYYY') : 'Beginning'} to {dayjs(data.toDate).format('MMM DD, YYYY')}
              </Text>
            </div>
          </>
        )}
      </Spin>
    </Card>
  );
};

export default TrialBalanceReport;